// @flow

import {
  chain,
  groupBy,
  indexBy,
  omit,
  prop,
  propEq,
  reverse,
  sortBy,
  union,
} from "ramda";
import { Maybe } from "ramda-fantasy";
import { createSelector } from "reselect";

import {
  type MedidaSelecionada,
  type EdicaoMedida,
  type Medida,
  type Id,
  isMedidaNome,
  ID_CONFIGURACAO_RELATORIO_MEDIDA_NOME,
  ID_CONFIGURACAO_RELATORIO_MEDIDA_FIE,
  ID_CONFIGURACAO_RELATORIO_MEDIDA_MEDIDA_USUARIO,
  ID_PERIODO_JANELA,
  ID_TIPO_INICIO_SERIE,
  ID_TIPO_FINAL_SERIE,
  GRUPAMENTO_MEDIDAS_QUALITATIVAS,
  MODO_APP_NORMAL,
  MODO_APP_SERIES,
  MODO_APP_EXCEL,
} from "../../model";
import {
  type State,
  getEdicaoMedida,
  getIdsPeriodosEstendidos,
  getMedidaPorId,
  getMedidasPorId,
  getModoApp,
  getGrupamentoMedidaSelecionado,
  getMedidas,
  getMedidasReferenciadasEmMedidasParaPersonalizar,
  getOrdemPorMedida,
  getPeriodoUnicoSelecionado,
  getPeriodoPorId,
  getSelecaoJanela,
  getTipoJanelaSelecionada,
  getExpressao,
  getModoExibicaoMedidaSelecionada,
  getMedidasRankeaveis,
} from "../../state";
import { getMedidasReferenciadasEmExpressoes } from "./edicaoMedidaSelectors";
import { randomId, isMultiplasMedidasSelecionadas } from "../../util";

import {
  loadingRemoteData,
  failedToLoadRemoteData,
} from "@quantum/remote-data";
import { getArquivoEmCache } from "./arquivosMedidasSelectors";

export const getMedidasSelecionadas = (
  state: State
): Array<MedidaSelecionada> => state.medidasSelecionadas;

export const isCheckedMedidaSelecionada = (
  state: State,
  indiceMedida: number
): boolean =>
  typeof indiceMedida === "number" &&
  Boolean(state.medidasSelecionadas[indiceMedida]) &&
  Boolean(state.medidasSelecionadas[indiceMedida].isChecked);

export type GeradorMedidasSelecionadas = (
  State,
  Array<MedidaSelecionada>
) => Array<MedidaSelecionada>;

export const comporGeradorDeMedidas = (
  ...geradores: Array<GeradorMedidasSelecionadas>
): GeradorMedidasSelecionadas => (state, medidas) =>
  reverse(geradores).reduce(
    (medidas, gerador) => gerador(state, medidas),
    medidas
  );

export const getMedidaSelecionadaNome = (state: State): MedidaSelecionada => {
  return getMedidaSelecionadaPorIdConfiguracaoRelatorio(
    state,
    ID_CONFIGURACAO_RELATORIO_MEDIDA_NOME
  );
};

export const getMedidaSelecionadaFIE = (state: State): MedidaSelecionada => {
  return getMedidaSelecionadaPorIdConfiguracaoRelatorio(
    state,
    ID_CONFIGURACAO_RELATORIO_MEDIDA_FIE
  );
};

export const getMedidaSelecionadaMedidaUsuario = (
  state: State
): MedidaSelecionada => {
  return getMedidaSelecionadaPorIdConfiguracaoRelatorio(
    state,
    ID_CONFIGURACAO_RELATORIO_MEDIDA_MEDIDA_USUARIO
  );
};

export const isMedidasSelecionadasEmEstadoInicial = createSelector(
  getMedidasSelecionadas,
  getMedidaSelecionadaFIE,
  getArquivoEmCache,
  getModoApp,
  (medidasSelecionadas, medidaFIE, arquivo, modo) => {
    // Como não é possível remover a medida Nome, se houver somente uma medida selecionada, estamos no estado inicial
    const somenteMedidaNome =
      medidasSelecionadas.length === 1 &&
      isMedidaNome(medidasSelecionadas[0].medida);
    const somenteMedidaFIE =
      medidasSelecionadas.length === 2 &&
      medidasSelecionadas.includes(medidaFIE);
    const nenhumaMedidaSelecionada = medidasSelecionadas.length === 0;

    switch (modo) {
      case MODO_APP_NORMAL:
        return !arquivo && (somenteMedidaNome || somenteMedidaFIE);
      case MODO_APP_SERIES:
        return !arquivo && nenhumaMedidaSelecionada;
      case MODO_APP_EXCEL:
        return nenhumaMedidaSelecionada;
      default:
        return false;
    }
  }
);

export const getIndiceMedidaFIESelecionada = (state: State): number => {
  return getMedidasSelecionadas(state).findIndex(
    medidaSelecionada =>
      medidaSelecionada.medida.idConfiguracaoRelatorio ===
      ID_CONFIGURACAO_RELATORIO_MEDIDA_FIE
  );
};

export const getMedidaSelecionadaPorIdConfiguracaoRelatorio = (
  state: State,
  id: number
): MedidaSelecionada => {
  const medida = getMedidas(state).find(
    medida => medida.idConfiguracaoRelatorio === id
  );

  return medida ? obterMedidaSelecionadaSimples(medida) : {};
};

export const getMedidasFixas: State => Array<MedidaSelecionada> = createSelector(
  prop("medidasFixas"),
  createSelector(getMedidas, indexBy(prop("idConfiguracaoRelatorio"))),
  (fixas, medidas) =>
    fixas
      .map(id => medidas[id])
      .filter(Boolean)
      .map(obterMedidaSelecionadaSimples)
);

export const criarSelectorMedidasSelecioandas = (state: State) => (
  geradorMedidasSelecionadas: GeradorMedidasSelecionadas
) => (): Array<MedidaSelecionada> => {
  const edicaoMedida: EdicaoMedida = getEdicaoMedida(state);
  const grupamentoMedidaSelecionado = getGrupamentoMedidaSelecionado(state);

  if (edicaoMedida.medida !== null && edicaoMedida.medida !== undefined) {
    return Maybe(
      grupamentoMedidaSelecionado === GRUPAMENTO_MEDIDAS_QUALITATIVAS
        ? // $FlowFixMe: Devido a maneira como EdicaoMedida foi definido, o Flow lança um erro ao usar um Array
          getMedidasPorId(state)(edicaoMedida.medida)
        : // $FlowFixMe: Devido a maneira como edicaoMedida foi definido, o Flow lança um erro ao usar um Array
        isMultiplasMedidasSelecionadas(edicaoMedida.medida) &&
          // $FlowFixMe: Devido a maneira como edicaoMedida foi definido, o Flow lança um erro ao usar um Array
          edicaoMedida.medida.length > 1 //MULTIPLASMEDIDAS
        ? // $FlowFixMe: Devido a maneira como edicaoMedida foi definido, o Flow lança um erro ao usar um Array
          getMedidasPorId(state)(edicaoMedida.medida)
        : // $FlowFixMe: Devido a maneira como edicaoMedida foi definido, o Flow lança um erro ao usar um Array
          [getMedidaPorId(state)(edicaoMedida.medida)]
    )
      .map(chain(medida => obterListaBaseMedidasSelecionadas(state, medida)))
      .map(medidasAdicionadas => {
        if (isMultiplasMedidasSelecionadas(geradorMedidasSelecionadas)) {
          const medidasFinal = medidasAdicionadas.map((medida, index) =>
            // $FlowFixMe: Devido a maneira como geradorMedidasSelecionadas foi definido, o Flow lança um erro ao usar um Array
            geradorMedidasSelecionadas[index](state, [medida]).pop()
          );
          return medidasFinal;
        } else {
          return geradorMedidasSelecionadas(state, medidasAdicionadas);
        }
      })
      .map(medidas =>
        medidas.map((medida, i) => {
          const medidaSelecionadaAtual = Maybe(edicaoMedida.indiceMedidaEditada)
            .map((indice: number) => getMedidasSelecionadas(state)[indice])
            .getOrElse({});

          const isMedidaDerivada =
            medida.medida.tipoMedidasSecundarias ||
            medida.medida.idConfiguracaoRelatorioMedidaPrimaria;

          const isPeriodoComposto = getIdsPeriodosEstendidos(state).includes(
            medida.periodo
          );

          const deveRestaurarNomeOriginal =
            isMedidaDerivada || isPeriodoComposto;

          const deveRemoverAlias =
            isMedidaDerivada || isPeriodoComposto || i > 0;

          return {
            ...omit(["label"], medida),
            ...(deveRestaurarNomeOriginal
              ? {}
              : { label: medidaSelecionadaAtual.label }),
            ...(medida.descricaoSelecao == null ? { label: medida.label } : {}),
            ...(deveRemoverAlias ? {} : { alias: edicaoMedida.alias }),
            isChecked: false,
            indiceMedidaEditada: edicaoMedida.indiceMedidaEditada,
          };
        })
      )
      .getOrElse([]);
  } else {
    return [];
  }
};

const gerarMedidasPeriodoSeries = (
  state: State,
  medida: Medida,
  secundarias: Array<MedidaSelecionada>,
  idGrupo: Id
): Array<MedidaSelecionada> => {
  const PERIODOS_MEDIDAS_SECUNDARIAS_PERIODO_SERIE = [
    getPeriodoPorId(state)(ID_TIPO_INICIO_SERIE).chaveCalculo,
    getPeriodoPorId(state)(ID_TIPO_FINAL_SERIE).chaveCalculo,
  ];

  const idPeriodoSelecionado: ?Id = getPeriodoUnicoSelecionado(state);
  const chaveCalculoPeriodo = idPeriodoSelecionado
    ? getPeriodoPorId(state)(idPeriodoSelecionado).chaveCalculo
    : "";

  if (
    PERIODOS_MEDIDAS_SECUNDARIAS_PERIODO_SERIE.indexOf(chaveCalculoPeriodo) >= 0
  ) {
    return secundarias.filter(
      medidaSecundaria =>
        medidaSecundaria.medida.nome != null &&
        medidaSecundaria.medida.nome
          .toLowerCase()
          .indexOf(chaveCalculoPeriodo.toLowerCase()) >= 0
    );
  } else {
    return [obterMedidaSelecionadaSimples(medida)];
  }
};

export function obterListaBaseMedidasSelecionadas(
  state: State,
  medida: Medida
): Array<MedidaSelecionada> {
  const medidasSecundarias: ?Array<Medida> = medida.medidasSecundarias;
  if (medidasSecundarias != null && medida.tipoMedidasSecundarias != null) {
    const idGrupo: Id = randomId();
    const secundariasNaoOrdenadas =
      medida.idGrupoMedidasSelecionadas == null
        ? medidasSecundarias.map(medida =>
            obterMedidaSelecionadaAgrupada(medida, idGrupo)
          )
        : medidasSecundarias.map(obterMedidaSelecionadaSimples);

    const secundarias = sortBy(
      ({ medida }) => getOrdemPorMedida(medida, state),
      secundariasNaoOrdenadas
    );

    switch (medida.tipoMedidasSecundarias) {
      case "DERIVADA":
        return [
          obterMedidaSelecionadaAgrupada(medida, idGrupo),
          ...secundarias,
        ];
      case "AGREGADA":
        return [...secundarias];
      case "PERIODO_SERIE":
        return gerarMedidasPeriodoSeries(state, medida, secundarias, idGrupo);
      default:
        return [obterMedidaSelecionadaSimples(medida)];
    }
  } else {
    return [obterMedidaSelecionadaSimples(medida)];
  }
}

function obterMedidaSelecionadaAgrupada(
  medida: Medida,
  idGrupoMedidasSelecionadas: Id
): MedidaSelecionada {
  return {
    ...obterMedidaSelecionadaSimples(medida),
    idGrupoMedidasSelecionadas,
  };
}

function obterMedidaSelecionadaSimples(medida: Medida): MedidaSelecionada {
  return {
    medida: medida,
    descricaoSelecao: medida.nome,
    isChecked: false,
    alias: "",
  };
}

export function obterMedidasQualitativasSelecionadas(
  state: State,
  medidasSelecionadas: Array<MedidaSelecionada>
): Array<MedidaSelecionada> {
  return medidasSelecionadas;
}

export const getAdicionarMedidaDesabilitado = (state: State) => {
  switch (getPeriodoUnicoSelecionado(state)) {
    case ID_PERIODO_JANELA: {
      const tipoJanelaSelecionada = getTipoJanelaSelecionada(state);
      const selecaoJanela = getSelecaoJanela(state);

      const loading: boolean = loadingRemoteData(
        "datasSelecaoJanela",
        { selecaoJanela, tipoJanelaSelecionada },
        state
      );
      const failed: boolean = failedToLoadRemoteData(
        "datasSelecaoJanela",
        { selecaoJanela, tipoJanelaSelecionada },
        state
      );

      const desabilitado = Boolean(failed || loading);

      return { failed, loading, desabilitado, mensagem: null };
    }

    default:
      switch (getModoExibicaoMedidaSelecionada(state)) {
        case "CRIAR_MEDIDA": {
          const properties = {
            expressao: getExpressao(state),
            medidasSelecionadas: getMedidasRankeaveis(state),
          };
          const loading: boolean = loadingRemoteData(
            "validacaoExpressaoSintaxe",
            properties,
            state
          );
          const failed: boolean = failedToLoadRemoteData(
            "validacaoExpressaoSintaxe",
            properties,
            state
          );

          const desabilitado = Boolean(failed || loading);

          return {
            failed,
            loading,
            desabilitado,
            mensagem: "Expressão inválida",
          };
        }

        default:
          return {
            failed: false,
            loading: false,
            desabilitado: false,
            mensagem: null,
          };
      }
  }
};

export const getMedidaSelecionadaEdicao = (
  state: State,
  indiceMedida: number
): MedidaSelecionada => {
  const medidaSelecionada: MedidaSelecionada = getMedidasSelecionadas(state)[
    indiceMedida
  ];
  const idMedidaPrimaria =
    medidaSelecionada.medida.idConfiguracaoRelatorioMedidaPrimaria;

  return Maybe(idMedidaPrimaria)
    .map(getMedidaPorId(state))
    .map(medida => ({ ...medidaSelecionada, medida }))
    .getOrElse(medidaSelecionada);
};

export const isTodasMedidasSelecionadasMarcadas = (state: State) => {
  const medidas = getMedidasSelecionadas(state);
  const medidasMarcaveis = medidas.filter(
    medida => !isMedidaNome(medida.medida)
  );
  return (
    medidasMarcaveis.length > 0 &&
    medidasMarcaveis.every(medida => medida.isChecked)
  );
};

const getMedidasMarcadas = createSelector(getMedidasSelecionadas, medidas =>
  medidas.filter(medida => medida.isChecked)
);

const getMedidasReferenciadas = createSelector(
  getMedidasReferenciadasEmExpressoes,
  getMedidasReferenciadasEmMedidasParaPersonalizar,
  (referenciadasEmExpressoes, referenciadasEmMedidasParaPersonalizar) =>
    new Set(
      union(
        [...referenciadasEmExpressoes],
        [...referenciadasEmMedidasParaPersonalizar]
      )
    )
);

const getMedidasDesabilitadasDeEdicao = createSelector(
  getMedidasSelecionadas,
  getMedidasReferenciadas,
  (medidasSelecionadas, medidasReferenciadas) =>
    medidasSelecionadas.filter(medidaSelecionada => {
      if (medidasReferenciadas.has(medidaSelecionada.alias)) {
        return true;
      }

      // Não permite medidas derivadas/agregadas
      if (medidaSelecionada.idGrupoMedidasSelecionadas != null) {
        return medidasSelecionadas
          .filter(
            propEq(
              "idGrupoMedidasSelecionadas",
              medidaSelecionada.idGrupoMedidasSelecionadas
            )
          )
          .some(medida => medidasReferenciadas.has(medida.alias));
      }

      return false;
    })
);

export const getMedidasPorGrupo = createSelector(
  getMedidasSelecionadas,
  groupBy(prop("idGrupoMedidasSelecionadas"))
);

const getAliasMedidasDesabilitadasDeEdicao = createSelector(
  getMedidasDesabilitadasDeEdicao,
  medidas => new Set(medidas.map(medida => medida.alias).filter(Boolean))
);

export const deveDesabilitarEdicaoDeMedida = (
  state: State,
  medidaSelecionada: MedidaSelecionada
): boolean =>
  getAliasMedidasDesabilitadasDeEdicao(state).has(medidaSelecionada.alias);

export const deveDesabilitarExclusaoMultiplasDeMedidas: State => boolean = createSelector(
  getMedidasMarcadas,
  getAliasMedidasDesabilitadasDeEdicao,
  (medidasMarcadas, medidasDesabilitadas) =>
    medidasMarcadas.some(medida => medidasDesabilitadas.has(medida.alias))
);
